import type {FilterOption} from "../reducers/includeExcludeFilterReducer";

export const MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH = 2;

export interface TreeFilterItem {
    id: string;
    label: string;
    option: FilterOption;
    itemLabel?: string;
    children?: TreeFilterItem[];
}

export interface SelectAllState {
    checked: boolean;
    indeterminate: boolean;
}

export const getDisplayLabel = (option: {item?: string; label?: string}): string =>
    option.label?.trim() || option.item?.trim() || "";

const getFilterOptionItemLabel = (option: FilterOption) => {
    const optionWithItemLabel = option as FilterOption & {itemLabel?: string};
    return optionWithItemLabel.itemLabel?.trim() || getDisplayLabel(option);
};

export const sortFilterOptions = (options: FilterOption[]): FilterOption[] =>
    [...options]
        .sort((a, b) => {
            const left = a.item?.trim() || a.label?.trim() || "";
            const right = b.item?.trim() || b.label?.trim() || "";
            return left.localeCompare(right, undefined, {sensitivity: "base"});
        })
        .map((option) => ({
            ...option,
            subItems: option.subItems ? sortFilterOptions(option.subItems) : undefined,
        }));

export const getExpandableItemIds = (items: TreeFilterItem[]): string[] => {
    const expanded: string[] = [];

    const visit = (treeItems: TreeFilterItem[]) => {
        treeItems.forEach((item) => {
            if (item.children && item.children.length > 0) {
                expanded.push(item.id);
                visit(item.children);
            }
        });
    };

    visit(items);
    return expanded;
};

export const areItemIdsEqual = (left: string[], right: string[]) =>
    left.length === right.length && left.every((itemId, index) => itemId === right[index]);

export const buildTreeItems = (
    options: FilterOption[],
    parentPath: string = "",
): TreeFilterItem[] =>
    options.map((option, index) => {
        const itemLabel = getFilterOptionItemLabel(option);
        const id = `${parentPath}${option.categoryId}-${itemLabel}-${index}`;
        return {
            id,
            label: itemLabel,
            option,
            itemLabel,
            children: option.subItems ? buildTreeItems(option.subItems, `${id}-`) : undefined,
        };
    });

export const filterTreeOptionsBySearchTerm = (
    options: FilterOption[],
    termToSearch: string,
): FilterOption[] => {
    if (!termToSearch || termToSearch.length < MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH) {
        return options;
    }

    const lowerSearch = termToSearch.toLowerCase();

    const searchRecursive = (
        option: FilterOption,
    ): {matched: boolean; filteredOpt: FilterOption | null} => {
        const itemMatches =
            (option.item?.trim() || option.label?.trim() || "").toLowerCase().includes(lowerSearch);

        const matchedSubItems: FilterOption[] = [];
        let hasMatchingDescendants = false;

        if (option.subItems?.length) {
            for (const subItem of option.subItems) {
                const {matched, filteredOpt} = searchRecursive(subItem);
                if (matched && filteredOpt) {
                    matchedSubItems.push(filteredOpt);
                    hasMatchingDescendants = true;
                }
            }
        }

        const shouldInclude = itemMatches || hasMatchingDescendants;

        if (shouldInclude) {
            return {
                matched: true,
                filteredOpt: {
                    ...option,
                    subItems: matchedSubItems.length > 0 ? matchedSubItems : option.subItems,
                },
            };
        }

        return {matched: false, filteredOpt: null};
    };

    return options.reduce<FilterOption[]>((results, option) => {
        const {matched, filteredOpt} = searchRecursive(option);
        if (matched && filteredOpt) {
            results.push(filteredOpt);
        }
        return results;
    }, []);
};

export interface NodeCheckState {
    checked: boolean;
    indeterminate: boolean;
}

/**
 * Tri-state for a tree node derived from the live selection. A node is checked
 * when every selectable descendant (and its own value, if selectable) is
 * checked, indeterminate when some — but not all — are. Cascade-only nodes are
 * structural, so they never contribute their own value to the count.
 */
export const computeNodeCheckState = (
    item: TreeFilterItem,
    isChecked: (option: FilterOption) => boolean,
): NodeCheckState => {
    const selectable: FilterOption[] = [];

    const walk = (node: TreeFilterItem) => {
        if (!node.option.cascadeOnly) {
            selectable.push(node.option);
        }
        node.children?.forEach(walk);
    };
    walk(item);

    const total = selectable.length;
    if (total === 0) {
        return {checked: false, indeterminate: false};
    }

    const checkedCount = selectable.reduce(
        (count, option) => count + (isChecked(option) ? 1 : 0),
        0,
    );

    return {
        checked: checkedCount === total,
        indeterminate: checkedCount > 0 && checkedCount < total,
    };
};

/**
 * Branch ids to auto-expand for a search. The tree is already pruned to matches
 * plus their ancestors, so every expandable node in it is a branch that
 * contains a match.
 */
export const getMatchingBranchIds = (filteredTree: TreeFilterItem[]): string[] =>
    getExpandableItemIds(filteredTree);

/** Distinct categoryIds owned by a (possibly nested) set of options. */
export const collectSubtreeCategoryIds = (options: FilterOption[]): string[] => {
    const ids = new Set<string>();
    const walk = (opts?: FilterOption[]) =>
        opts?.forEach((option) => {
            ids.add(option.categoryId);
            walk(option.subItems);
        });
    walk(options);
    return [...ids];
};

export type DraftFilterMap = Record<string, string[]>;

export interface DraftFilterSelection {
    include: DraftFilterMap;
    exclude: DraftFilterMap;
}

/**
 * Pure, reducer-mirroring cascade toggle for a local popover draft. Toggling a
 * node flips its own value (unless cascade-only) and cascades to every
 * descendant leaf grouped by categoryId; the same values are always removed
 * from the opposite include/exclude side so the two sets stay independent.
 * Mirrors handleToggleOption in includeExcludeFilterReducer so the dialog and
 * chip popover behave identically.
 */
export const cascadeToggleDraft = (
    draft: DraftFilterSelection,
    filterType: "include" | "exclude",
    option: FilterOption,
): DraftFilterSelection => {
    const opposite = filterType === "include" ? "exclude" : "include";
    const active: DraftFilterMap = {...draft[filterType]};
    const other: DraftFilterMap = {...draft[opposite]};

    const addTo = (map: DraftFilterMap, categoryId: string, item: string) => {
        const current = map[categoryId] ?? [];
        if (!current.includes(item)) {
            map[categoryId] = [...current, item];
        }
    };
    const removeFrom = (map: DraftFilterMap, categoryId: string, item: string) => {
        const current = map[categoryId];
        if (!current) return;
        const next = current.filter((value) => value !== item);
        if (next.length > 0) {
            map[categoryId] = next;
        } else {
            delete map[categoryId];
        }
    };

    const descendantsByCategory: DraftFilterMap = {};
    const collect = (subItems?: FilterOption[]) =>
        subItems?.forEach((subItem) => {
            if (!subItem.cascadeOnly) {
                (descendantsByCategory[subItem.categoryId] ??= []).push(subItem.item);
            }
            collect(subItem.subItems);
        });
    collect(option.subItems);

    let willBeChecked: boolean;
    if (!option.cascadeOnly) {
        willBeChecked = !(active[option.categoryId] ?? []).includes(option.item);
    } else {
        const descendants = Object.entries(descendantsByCategory).flatMap(
            ([categoryId, items]) => items.map((item) => ({categoryId, item})),
        );
        const allChecked =
            descendants.length > 0 &&
            descendants.every(({categoryId, item}) =>
                (active[categoryId] ?? []).includes(item),
            );
        willBeChecked = descendants.length > 0 && !allChecked;
    }

    if (!option.cascadeOnly) {
        if (willBeChecked) {
            addTo(active, option.categoryId, option.item);
        } else {
            removeFrom(active, option.categoryId, option.item);
        }
        removeFrom(other, option.categoryId, option.item);
    }

    Object.entries(descendantsByCategory).forEach(([categoryId, items]) => {
        items.forEach((item) => {
            if (willBeChecked) {
                addTo(active, categoryId, item);
            } else {
                removeFrom(active, categoryId, item);
            }
            removeFrom(other, categoryId, item);
        });
    });

    return filterType === "include"
        ? {include: active, exclude: other}
        : {include: other, exclude: active};
};

